
import React, { useState } from 'react';
import { Info, ThumbsUp, ThumbsDown, X, Sparkle } from 'lucide-react';
import { useArticleAI } from '@/contexts/article-ai/ArticleAIContext';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ArticleElaborateFeatureProps {
  paragraphText: string;
  paragraphIndex: number;
}

const ArticleElaborateFeature: React.FC<ArticleElaborateFeatureProps> = ({ 
  paragraphText, 
  paragraphIndex 
}) => {
  const { elaborateOnParagraph, elaborations, elaborationLoadingStates, provideFeedback } = useArticleAI();
  const [showElaboration, setShowElaboration] = useState(false);

  const handleElaborateClick = async () => {
    if (!elaborations[paragraphIndex] && !elaborationLoadingStates[paragraphIndex]) {
      await elaborateOnParagraph(paragraphText, paragraphIndex);
    }
    setShowElaboration(true);
  };

  const isLoading = elaborationLoadingStates[paragraphIndex];
  const elaboration = elaborations[paragraphIndex];

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "transition-all duration-300 group-hover:scale-105 absolute right-0 top-1/2 -translate-y-1/2 mr-2",
                "opacity-0 group-hover:opacity-100 h-8 shadow-md border border-primary/30",
                "bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200",
                "dark:from-blue-900/20 dark:to-blue-800/30 dark:hover:from-blue-800/30 dark:hover:to-blue-700/40"
              )}
              onClick={handleElaborateClick}
            >
              <Sparkle className="h-3.5 w-3.5 mr-1 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300">More context</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/90 dark:border-blue-700 dark:text-blue-200">
            <p>Get AI-powered insights about this paragraph</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {showElaboration && (
        <div className="mt-2 mb-4 p-4 bg-blue-50/70 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 relative animate-fade-in shadow-md">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 absolute top-2 right-2 hover:bg-blue-100/80 dark:hover:bg-blue-800/30"
            onClick={() => setShowElaboration(false)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>

          {isLoading ? (
            <div className="py-6 flex justify-center items-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                <p className="text-sm text-blue-600/70 dark:text-blue-400/70">Generating insights...</p>
              </div>
            </div>
          ) : elaboration ? (
            <>
              <div className="text-sm whitespace-pre-wrap pt-1">{elaboration}</div>
              <div className="flex justify-between mt-3 pt-2 border-t border-blue-200/50 dark:border-blue-700/30">
                <div className="text-xs text-blue-600/70 dark:text-blue-400/70 flex items-center">
                  <Info className="h-3 w-3 mr-1" />
                  AI-generated insights
                </div>
                <div className="flex gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 dark:text-blue-300"
                          onClick={() => provideFeedback('elaborate', paragraphIndex, 'helpful')}
                        >
                          <ThumbsUp className="h-3 w-3 mr-1" />
                          <span className="text-xs">Helpful</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>This was helpful</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 border-red-200 bg-red-50 hover:bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-300"
                          onClick={() => provideFeedback('elaborate', paragraphIndex, 'not-helpful')}
                        >
                          <ThumbsDown className="h-3 w-3 mr-1" />
                          <span className="text-xs">Not helpful</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>This wasn't helpful</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-blue-600/70 dark:text-blue-400/70 text-center py-4">
              Unable to generate insights for this paragraph
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default ArticleElaborateFeature;
